import { ContextType } from "@/services/bedrock/dtos/DesignPackage";
import type { DesignBasis } from "./NewDesignPage";

export interface StyleChip {
  id: string;
  group: "layout" | "imagery" | "mood" | "typography" | "details";
  label: string;
  fragment: string;
  allowedKinds: ContextType[];
  allowedFormats: ("single" | "carousel")[];
  exclusiveGroup?: string;
}

const ALL_KINDS: ContextType[] = ["review", "player", "none"];
const BOTH_FORMATS: StyleChip["allowedFormats"] = ["single", "carousel"];

export const STYLE_GROUPS: { id: StyleChip["group"]; label: string }[] = [
  { id: "layout", label: "Layout" },
  { id: "imagery", label: "Background" },
  { id: "mood", label: "Mood" },
  { id: "typography", label: "Typography" },
  { id: "details", label: "Details" },
];

type Season = "spring" | "summer" | "fall" | "winter";

const SEASON_SCENES: Record<Season, string> = {
  spring: "fresh greenery and blooming flowers",
  summer: "full green trees and bright summer color",
  fall: "autumn leaves and warm fall color",
  winter: "a crisp winter feel",
};

// Northern Hemisphere by month, matching the background matcher's own season bonus.
export function seasonFor(date: Date): Season {
  const month = date.getMonth() + 1;
  if (month >= 3 && month <= 5) return "spring";
  if (month >= 6 && month <= 8) return "summer";
  if (month >= 9 && month <= 11) return "fall";
  return "winter";
}

const SEASON_ORDER: Season[] = ["spring", "summer", "fall", "winter"];

/** The season after `season`, for posts planned ahead. */
export function nextSeason(season: Season): Season {
  return SEASON_ORDER[(SEASON_ORDER.indexOf(season) + 1) % SEASON_ORDER.length];
}

const SEASON = seasonFor(new Date());
const NEXT_SEASON = nextSeason(SEASON);

// Order matters: the brief lists chips in this order within each group.
export const STYLE_CHIPS: StyleChip[] = [
  {
    id: "glass",
    group: "layout",
    label: "Frosted glass card",
    fragment:
      "a frosted-glass card with backdrop blur, a thin white border, and a soft shadow floats over the background",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "split",
    group: "layout",
    label: "Split screen",
    fragment:
      "the card is split vertically: one half is the photo, the other half is a solid brand-color panel holding the text",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "polaroid",
    group: "layout",
    label: "Polaroid frame",
    fragment: "the photo sits in an offset, slightly rotated Polaroid frame on a textured canvas background",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "arch",
    group: "layout",
    label: "Arched photo frame",
    fragment: "the photo sits inside a tall arched, window-shaped frame on a solid brand-color background",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "magazine",
    group: "layout",
    label: "Magazine cover",
    fragment:
      "laid out like a lifestyle magazine cover: a large masthead-style headline across the top of a full-bleed photo",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "outlined-box",
    group: "layout",
    label: "Thin outlined box",
    fragment: "the main text sits inside a thin outlined box with generous negative space",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "widget",
    group: "layout",
    label: "App widget look",
    fragment: "structured like a clean mobile app widget: bordered, rounded containers and a floating profile pill",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "layout-shell",
  },
  {
    id: "ribbon",
    group: "layout",
    label: "Ribbon that flows across slides",
    fragment: "a continuous sleek geometric ribbon runs across all slides so the cards connect when swiped",
    allowedKinds: ALL_KINDS,
    allowedFormats: ["carousel"],
  },
  {
    id: "intro-outro",
    group: "layout",
    label: "Intro and ending slides",
    fragment:
      "open with a bold hook slide and close with an ending slide that has the player's profile and a call to action",
    allowedKinds: ALL_KINDS,
    allowedFormats: ["carousel"],
  },
  {
    id: "numbered-steps",
    group: "layout",
    label: "One numbered tip per slide",
    fragment: "each slide after the hook is one numbered tip or step with a large numeral, like a short how-to guide",
    allowedKinds: ["player", "none"],
    allowedFormats: ["carousel"],
  },
  // Background chips name one library setting each, in the library's own words, and rule out
  // people: the matcher fills them from published backgrounds instead of generating new ones.
  {
    id: "home-photo",
    group: "imagery",
    label: "Home exterior",
    fragment: "a full-bleed photo of a home's exterior and front yard, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "interior-photo",
    group: "imagery",
    label: "Home interior",
    fragment: "a full-bleed photo of a bright, styled home interior such as a living room or kitchen, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "keys-photo",
    group: "imagery",
    label: "Keys and closing day",
    fragment:
      "a full-bleed closing-day photo of new house keys, a front door, or a welcome mat, with no people or hands",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "moving-photo",
    group: "imagery",
    label: "Moving day",
    fragment: "a full-bleed moving-day photo of boxes in a sunny new home, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "neighborhood-photo",
    group: "imagery",
    label: "Neighborhood street",
    fragment: "a full-bleed photo of a tree-lined neighborhood street of homes, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "nature-photo",
    group: "imagery",
    label: "Scenic outdoors",
    fragment: "a full-bleed scenic outdoor photo such as a beach, a mountain lake, or open countryside, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "city-photo",
    group: "imagery",
    label: "City skyline",
    fragment: "a full-bleed photo of a city skyline, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "desk-photo",
    group: "imagery",
    label: "Desk and planning",
    fragment: "a full-bleed photo of a tidy desk with a notebook, a calculator, or a small house model, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "texture-photo",
    group: "imagery",
    label: "Subtle texture",
    fragment:
      "a full-bleed close-up photo of a natural surface such as linen, marble, plaster, or wood grain, with no people",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "brand-only",
    group: "imagery",
    label: "No photo, brand color only",
    fragment: "no background photo; use only the branch's brand colors",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-bg",
  },
  {
    id: "seasonal",
    group: "imagery",
    label: `Set in ${SEASON}`,
    fragment: `the photo is set in ${SEASON}, with ${SEASON_SCENES[SEASON]}`,
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-season",
  },
  {
    id: "next-season",
    group: "imagery",
    label: `Set in ${NEXT_SEASON}`,
    fragment: `the photo is set in ${NEXT_SEASON}, with ${SEASON_SCENES[NEXT_SEASON]}`,
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-season",
  },
  // Moods use the library's mood wording (clean, warm, calm, bold) so they count toward a match.
  {
    id: "light-airy",
    group: "mood",
    label: "Light and airy",
    fragment: "a crisp, airy, uncluttered look with plenty of white space",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-tone",
  },
  {
    id: "warm",
    group: "mood",
    label: "Warm and inviting",
    fragment: "a warm and inviting feel, like soft golden-hour light",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-tone",
  },
  {
    id: "calm",
    group: "mood",
    label: "Calm and muted",
    fragment: "a soft, muted, and serene feel",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-tone",
  },
  {
    id: "bold",
    group: "mood",
    label: "Rich and vivid",
    fragment: "a clear and crisp look with rich but natural color",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-tone",
  },
  {
    id: "dark-mode",
    group: "mood",
    label: "Dark mode",
    fragment: "a dark palette built from deep shades of the brand's primary color",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "imagery-tone",
  },
  {
    id: "neon",
    group: "mood",
    label: "Neon glow accent",
    fragment: "a soft blurred neon accent gradient behind the main text",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "serif-quote",
    group: "typography",
    label: "Big serif quotation mark",
    fragment: "a massive, elegant serif quotation mark and thin editorial serif type",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "type-style",
  },
  {
    id: "chunky",
    group: "typography",
    label: "Bold chunky headline",
    fragment: "massive edge-to-edge chunky sans-serif type (Bebas Neue or Oswald) in high-contrast colors",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "type-style",
  },
  {
    id: "friendly-sans",
    group: "typography",
    label: "Clean friendly sans-serif",
    fragment: "a clean, friendly sans-serif with comfortable line spacing",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
    exclusiveGroup: "type-style",
  },
  {
    id: "script-accent",
    group: "typography",
    label: "Handwritten script accent",
    fragment: "one key word of the headline is set in an elegant handwritten script as an accent",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "highlight-phrase",
    group: "typography",
    label: "Highlight the best phrase",
    fragment: "the strongest phrase of the review quote is emphasized in the accent color",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "about-player",
    group: "details",
    label: "Make it about the player",
    fragment: "the design is centered on the player: name, headshot, and title front and center",
    allowedKinds: ["player", "none"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "headshot-overlap",
    group: "details",
    label: "Player's headshot overlapping the card",
    fragment: "the player's circular headshot overlaps the bottom edge of the main card",
    allowedKinds: ["review", "player"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "profile-pill",
    group: "details",
    label: "Name, title, and NMLS pill",
    fragment: "the player's name, title, and NMLS line sit in a rounded profile pill",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "branch-logo",
    group: "details",
    label: "Branch logo in a corner",
    fragment: "the branch logo sits in a corner",
    allowedKinds: ALL_KINDS,
    allowedFormats: BOTH_FORMATS,
  },
  // Stars come before the badge so the brief reads "stars; badge", matching the brief examples.
  {
    id: "stars",
    group: "details",
    label: "Five gold stars",
    fragment: "the star rating shows as five gold star icons",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "verified-badge",
    group: "details",
    label: "Verified client badge",
    fragment: "a small green Verified Client badge next to the reviewer's name",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "platform",
    group: "details",
    label: "Show the review platform",
    fragment: "the review platform's name and icon appear under the reviewer's name",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "chat-bubble",
    group: "details",
    label: "Quote in a text-message bubble",
    fragment: "the review quote sits inside a realistic rounded incoming text-message bubble",
    allowedKinds: ["review"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "big-cta",
    group: "details",
    label: "Big headline with a call to action",
    fragment: "a big headline with a clear call-to-action line under it",
    allowedKinds: ["none"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "celebration-banner",
    group: "details",
    label: "Celebration corner banner",
    fragment: 'a bold corner banner with a short celebratory phrase like "Just Closed" or "Welcome Home"',
    allowedKinds: ["none"],
    allowedFormats: BOTH_FORMATS,
  },
  {
    id: "swipe-cue",
    group: "details",
    label: "Swipe arrow",
    fragment: "a small swipe arrow on every slide except the last",
    allowedKinds: ALL_KINDS,
    allowedFormats: ["carousel"],
  },
  {
    id: "tagline",
    group: "details",
    label: "Short tagline about the player",
    fragment: "a short tagline line under the player's name",
    allowedKinds: ["player"],
    allowedFormats: BOTH_FORMATS,
  },
];

function allowedForBasis(chip: StyleChip, basis: DesignBasis): boolean {
  return basis === "review"
    ? chip.allowedKinds.includes("review")
    : chip.allowedKinds.includes("player") || chip.allowedKinds.includes("none");
}

/** Chips a coach may pick for this basis and format, in catalogue order. */
export function chipsFor(basis: DesignBasis, format: "single" | "carousel"): StyleChip[] {
  return STYLE_CHIPS.filter(chip => allowedForBasis(chip, basis) && chip.allowedFormats.includes(format));
}

export function deriveContextType(basis: DesignBasis, chipIds: string[]): ContextType {
  if (basis === "review") return "review";
  return chipIds.includes("about-player") ? "player" : "none";
}

export function pruneChipIds(chipIds: string[], basis: DesignBasis, format: "single" | "carousel"): string[] {
  const visible = new Set(chipsFor(basis, format).map(chip => chip.id));
  return chipIds.filter(id => visible.has(id));
}

/** Adds or removes `chip`; adding one from an exclusive group removes the other selected chip in it. */
export function toggleChip(chipIds: string[], chip: StyleChip): string[] {
  if (chipIds.includes(chip.id)) return chipIds.filter(id => id !== chip.id);
  const rivals = chip.exclusiveGroup
    ? new Set(STYLE_CHIPS.filter(c => c.exclusiveGroup === chip.exclusiveGroup).map(c => c.id))
    : new Set<string>();
  return [...chipIds.filter(id => !rivals.has(id)), chip.id];
}
